import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import {
  deliveryPointWithTimeFrameStartingAt,
  deliveryPointWithTimeFrameStartingToday,
  mapEmailNotificationSettingsToFormValues,
  mapFormValuesToRequestBody,
  newLoadingDeliveryPointFormValues,
  OrderForm
} from '../OrderForm'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import { createOrder, OrderEmailsLanguage } from 'services/OrderService'
import UserRole from 'constants/UserRole'
import { ORDER_COST_READ, ORDER_INCOME_READ, useAuth } from 'context/auth'
import moment from 'moment'
import { HTTP_404_ERROR, HTTP_422_ERROR } from 'Constants'
import { useErrorHandling } from 'context/errorHandling'
import {
  DISPATCHER_NOT_FOUND,
  FORWARDER_NOT_FOUND,
  INVALID_DELIVERY_METHOD,
  INVALID_DELIVERY_POINTS
} from 'services/errorCodes'
import ApiError from 'constants/ApiError'
import { useNavigatePrevent } from 'context/navigatePrevent'
import { fetchOrderAndFormRelatedData } from '../dataFetch'
import { orderFormPointsOf } from '../formValues'
import { ScheduledEmailsFrequencyOptions } from 'components/Order/constants'
import {
  newTransactionFormValues
} from 'components/Order/OrderForms/OrderForm/OrderTransactionsForm/OrderTransactionsGroupForm/OrderTransactionForm/formValues'
import {
  TransactionType
} from 'components/Order/OrderForms/OrderForm/OrderTransactionsForm/OrderTransactionsGroupForm/OrderTransactionForm/TransactionType'
import { CarrierFormValues } from 'components/Order/OrderForms/OrderForm/CarrierForm'

function getLoggedInUserByRole (role, auth) {
  return auth?.hasRole(role)
    ? auth.user
    : null
}

function getInitialValuesOfNewOrder (auth) {
  const loggedInForwarder = getLoggedInUserByRole(UserRole.Forwarder, auth)
  const loggedInDispatcher = getLoggedInUserByRole(UserRole.Dispatcher, auth)
  const bccEmails = []
  if (loggedInForwarder) {
    bccEmails.push(loggedInForwarder.email)
  }
  if (loggedInDispatcher) {
    bccEmails.push(loggedInDispatcher.email)
  }
  return ({
    referenceId: '',
    points: [newLoadingDeliveryPointFormValues()],
    transactions: {
      incomes: auth.hasAnyPrivileges([ORDER_INCOME_READ]) ? [newTransactionFormValues(TransactionType.Income)] : [],
      costs: auth.hasAnyPrivileges([ORDER_COST_READ]) ? [newTransactionFormValues(TransactionType.Cost)] : []
    },
    contractorInfo: {
      clientId: null,
      payerId: null,
      emails: [],
      bccEmails: bccEmails
    },
    emailNotificationSettings: {
      language: OrderEmailsLanguage.English,
      scheduledEmailsFrequency: ScheduledEmailsFrequencyOptions.MorningAndEvening
    },
    attachments: [],
    deletedAttachmentsIds: [],
    carrier: CarrierFormValues.noCarrierSelected(),
    forwarderId: loggedInForwarder?.id || null,
    dispatcherId: loggedInDispatcher?.dispatcherId || null,
    comment: '',
    printComment: '',
    highValue: false,
  })
}

function getInitialValuesOfCopiedOrder (auth, order, forwarderOfOrder, dispatcherOfOrder, ferryServices) {
  const {
    attachments,
    readableId,
    referenceId,
    deliveryMethod,
    loadingDeliveryPoints,
    unloadingDeliveryPoints,
    intermediatePoints,
    transactions,
    forwarderId,
    dispatcherId,
    contractorInfo,
    emailNotificationSettings,
    ...rest
  } = order
  const [offsetLoadingDeliveryPoints, offsetUnloadingDeliveryPoints]
    = offsetDeliveryPointsOfCopiedOrder(loadingDeliveryPoints, unloadingDeliveryPoints)
  const points = orderFormPointsOf(offsetLoadingDeliveryPoints, offsetUnloadingDeliveryPoints,
    intermediatePoints, ferryServices)

  const loggedInForwarder = getLoggedInUserByRole(UserRole.Forwarder, auth)
  const loggedInDispatcher = getLoggedInUserByRole(UserRole.Dispatcher, auth)
  const orderIncomes = transactions?.filter(t => t.transactionType === TransactionType.Income)
  const orderCosts = transactions?.filter(t => t.transactionType === TransactionType.Cost)
  let bccEmails = contractorInfo.bccEmails
  let newEmail = null
  if (loggedInForwarder) {
    if (forwarderOfOrder) {
      bccEmails = bccEmails.filter(email => email !== forwarderOfOrder.email)
    }
    newEmail = loggedInForwarder.email
  }
  if (loggedInDispatcher) {
    if (dispatcherOfOrder) {
      bccEmails = bccEmails.filter(email => email !== dispatcherOfOrder.email)
    }
    newEmail = loggedInDispatcher.email
  }
  if (newEmail && !bccEmails.includes(newEmail)) {
    bccEmails = [...bccEmails, newEmail]
  }

  const initialIncomes = auth.hasAnyPrivileges([ORDER_INCOME_READ]) ? [newTransactionFormValues(TransactionType.Income)] : []
  const initialCosts = auth.hasAnyPrivileges([ORDER_COST_READ]) ? [newTransactionFormValues(TransactionType.Cost)] : []
  return {
    ...rest,
    transactions: {
      incomes: orderIncomes.length > 0 ? orderIncomes : initialIncomes,
      costs: orderCosts.length > 0 ? orderCosts : initialCosts
    },
    contractorInfo: {
      ...contractorInfo,
      bccEmails: bccEmails
    },
    attachments: [],
    readableId: '',
    referenceId: '',
    carrier: CarrierFormValues.fromDeliveryMethod(deliveryMethod),
    forwarderId: loggedInForwarder?.id || forwarderId,
    dispatcherId: loggedInDispatcher?.id || dispatcherId,
    points,
    emailNotificationSettings: mapEmailNotificationSettingsToFormValues(emailNotificationSettings)
  }
}

function offsetDeliveryPointsOfCopiedOrder (
  loadingDeliveryPoints, unloadingDeliveryPoints) {
  const offsetLoadingDeliveryPoints = [], offsetUnloadingDeliveryPoints = []
  let i = 0, j = 0
  for (; i < loadingDeliveryPoints.length && j <
  unloadingDeliveryPoints.length; i++, j++) {
    const ldp = loadingDeliveryPoints[i]
    const udp = unloadingDeliveryPoints[j]
    const loadingStart = moment(ldp.fromDateTime)
    const unloadingStart = moment(udp.fromDateTime)
    const diff = unloadingStart.diff(loadingStart)
    const newLoadingStart = moment()
      .hours(loadingStart.hours())
      .minutes(loadingStart.minutes())
    const newUnloadingStart = newLoadingStart.clone()
      .add(diff)
    const offsetLdp = deliveryPointWithTimeFrameStartingAt(ldp, newLoadingStart)
    const offsetUdp = deliveryPointWithTimeFrameStartingAt(udp,
      newUnloadingStart)
    offsetLoadingDeliveryPoints.push(offsetLdp)
    offsetUnloadingDeliveryPoints.push(offsetUdp)
  }
  for (; i < loadingDeliveryPoints.length; i++) {
    const ldp = loadingDeliveryPoints[i]
    const offsetLdp = deliveryPointWithTimeFrameStartingToday(ldp)
    offsetLoadingDeliveryPoints.push(offsetLdp)
  }
  for (; j < unloadingDeliveryPoints.length; j++) {
    const udp = unloadingDeliveryPoints[j]
    const offsetUdp = deliveryPointWithTimeFrameStartingToday(udp)
    offsetUnloadingDeliveryPoints.push(offsetUdp)
  }
  return [offsetLoadingDeliveryPoints, offsetUnloadingDeliveryPoints]
}

export function CreateOrder (props) {
  const [initialValuesOfCopiedOrder, setInitialValuesOfCopiedOrder] = useState(null)
  const [initialValuesOfNewOrder, setInitialValuesOfNewOrder] = useState(null)
  const [savedOrderId, setSavedOrderId] = useState(null)
  const { t } = useTranslation()
  const auth = useAuth()

  const idOfCopiedOrder = props.location.state?.idOfCopiedOrder
  const { handleError } = useErrorHandling()
  const { setNavigationUnlocked } = useNavigatePrevent()

  useEffect(() => {
    if (!initialValuesOfNewOrder) {
      setInitialValuesOfNewOrder(getInitialValuesOfNewOrder(auth))
    }
  }, [initialValuesOfNewOrder, auth])

  useEffect(() => {
      if (idOfCopiedOrder && !initialValuesOfCopiedOrder) {
        fetchOrderAndFormRelatedData({ orderId: idOfCopiedOrder })
          .then(({ order, forwarder, dispatcher, ferryServices }) => {
              setInitialValuesOfCopiedOrder(
                getInitialValuesOfCopiedOrder(auth, order, forwarder, dispatcher, ferryServices))
            }
          ).catch(error => handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.orderNotFound'
          }).then(() => {
            setNavigationUnlocked(history => history.push('/orders'))
          })
        )
      }
    }, [idOfCopiedOrder, auth, initialValuesOfCopiedOrder, handleError, setNavigationUnlocked]
  )

  function handleSubmit (values, onSuccess) {
    const request = mapFormValuesToRequestBody(values)

    return createOrder(request)
      .then(createdOrder => onSuccess(createdOrder.id)
        .then(() => {
          setSavedOrderId(createdOrder.id)
          setNavigationUnlocked(history => history.push('/orders'))
        })
        .catch(handleError)
      )
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        handleError(error, {
          [HTTP_422_ERROR]: errorCode === INVALID_DELIVERY_METHOD ?
            'notifications.error.invalidDeliveryMethod' :
            errorCode === INVALID_DELIVERY_POINTS ?
              'notifications.error.invalidDeliveryPoints' :
              errorCode === FORWARDER_NOT_FOUND ?
                'notifications.error.forwarderNotFound' :
                errorCode === DISPATCHER_NOT_FOUND ?
                  'notifications.error.dispatcherNotFound' :
                  ApiError.UnprocessableEntity
        })
      })
  }

  const initialValues = idOfCopiedOrder
    ? initialValuesOfCopiedOrder
    : initialValuesOfNewOrder
  return ((!idOfCopiedOrder && initialValuesOfNewOrder) || initialValuesOfCopiedOrder ?
      <>
        <SuccessDialog open={!!savedOrderId}
                       onClose={() => {
                         setNavigationUnlocked(history => history.push(
                           savedOrderId
                             ? `/orders/details/${savedOrderId}`
                             : '/orders')
                         )
                       }}
                       content={t('notifications.success.createdOrder')}/>
        <OrderForm initialValues={initialValues}
                   onSubmit={handleSubmit}
                   title={'form.order.create'}>
        </OrderForm>
      </>
      :
      <Loader/>
  )
}
